import { useCallback, useEffect, useId, useRef, useState } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import { Eye, Pencil } from "lucide-react";
import { wcWizardEnabled } from "@/config";
import type { DocumentPrefillNavState } from "@/pages/submissions/NewSubmissionPage";
import { useAuth } from "@/auth/AuthProvider";
import { SubmissionFlowProgress } from "@/components/submissions/SubmissionFlowProgress";
import { ManifestDetailsForm } from "@/components/submissions/ManifestDetailsForm";
import { QuoteComparison } from "@/components/submissions/QuoteComparison";
import { SubmissionActions } from "@/components/submissions/SubmissionActions";
import { waitingOnBroker } from "@/lib/submissions/actionItems";
import { useActionItems } from "@/lib/submissions/useActionItems";
import { PreviewSubmission } from "@/components/submissions/wc/PreviewSubmission";
import { useLossRuns } from "@/components/submissions/lossRuns/lossRunsContext";
import {
  LossRunsButton,
  LossRunsPill,
} from "@/components/submissions/lossRuns/LossRunsHeader";
import { LossRunsProvider } from "@/components/submissions/lossRuns/LossRunsProvider";
import { AccountForm } from "@/components/AccountForm";
import { deriveFlowStep } from "@/lib/submissions/submissionFlow";
import {
  getAccount,
  updateAccount,
  accountMeetsFloor,
  unmetAccountFields,
  type AccountDetail,
  type AccountFloorField,
} from "@/lib/brokerAccounts";
import {
  getSubmission,
  getSubmissionCarriers,
  listSubmissionQuotes,
  setSubmissionDetails,
  type BrokerQuote,
  type DeclinedCarrier,
  type SubmissionCarrier,
  type SubmissionDetail,
} from "@/lib/submissions/brokerSubmissions";
import {
  getProducts,
  accountFloorFields,
  type MissingField,
  type ProductManifest,
} from "@/lib/brokerProducts";
import { messageFor } from "@/lib/errors";
import {
  handedIndications,
  handedRequests,
  missingFieldPath,
  WC_LAST_STEP,
  wcStepPath,
} from "@/lib/submissions/wc/review";
import { usePreviewPanel } from "@/lib/submissions/usePreviewPanel";
import { useLossRunsPanel } from "@/lib/submissions/useLossRunsPanel";
import { PageHeader } from "@/components/PageHeader";
import { usePageTrailName } from "@/components/PageTrail";
import { SubmissionStatusChip } from "@/components/submissions/SubmissionStatusChip";
import { RecordAvatar, StatStrip } from "@/base";
import { formatIsoDate } from "@/lib/displayFormat";
import { initialsOf } from "@/lib/initials";
import { formatShortDate } from "@/lib/listFormat";
import { insuredEditable } from "@/lib/submissions/wc/business";
import { wcFacts } from "@/lib/submissions/wc/facts";

import { buttonClass, TOUCH_TARGET } from "@/components/buttonStyles";
// Broker submission detail + quoting view (BRK-S040, unified in BRK-E003). Shows
// the submission header + the flow stepper, then — once quote-ready — the
// QuoteComparison table (BRK-S049) which auto-pulls the price indication and
// drives the quote flow. Before quote-readiness it shows the MANIFEST-
// DRIVEN details step (BRK-S052 — the product's indication floor off the
// catalog manifest, prefilled from the submission's field bag) or, for an
// incomplete insured, the insured-profile form inline (BRK-S046 gate →
// BRK-S050 inline form). Every not-ready state is handled, never a
// white-screen (NN #7).
// The detail page collapses the six flow steps into the three panels it can
// show (BRK-S060). "profile" = the insured-profile step (2), "rating" = the
// manifest details step (3), "pricing" = the quoting view (steps 4-6).
type FlowStage = "profile" | "rating" | "pricing";
const STAGE_LABEL: Record<FlowStage, string> = {
  profile: "Insured profile",
  rating: "Rating details",
  pricing: "Pricing & quotes",
};
const STAGE_EDIT_LABEL: Record<FlowStage, string> = {
  profile: "Edit insured profile",
  rating: "Edit rating details",
  pricing: "Pricing & quotes",
};
function stageOfStep(step: number): FlowStage {
  return step <= 2 ? "profile" : step === 3 ? "rating" : "pricing";
}

// BRK-S464 — the submission's tabs, in the URL.
const TAB_PARAM = "tab";
const ACTION_PARAM = "action";
type PageTab = "quotes" | "actions";

/** "Actions", with how many wait on the broker's reply. */
function ActionsTabLabel({ waiting }: { waiting: number }) {
  return (
    <span className="inline-flex items-center gap-2">
      Actions
      {waiting > 0 && (
        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700 dark:bg-amber-400/15 dark:text-amber-300">
          {waiting}
          <span className="sr-only"> waiting on you</span>
        </span>
      )}
    </span>
  );
}

export function SubmissionDetailPage() {
  const { id = "" } = useParams();
  const { authedRequest } = useAuth();
  // The document-intake prefill handed over from New Business (BRK-S038), never
  // persisted (no auto-commit): the details form marks it for review, and the
  // Save button is the explicit confirmation. Absent on every other entry path.
  const location = useLocation();
  const navigate = useNavigate();
  const documentPrefill = (location.state as DocumentPrefillNavState | null)
    ?.documentPrefill;

  const [detail, setDetail] = useState<SubmissionDetail | null>(null);
  const [quotes, setQuotes] = useState<BrokerQuote[]>([]);
  const [declined, setDeclined] = useState<DeclinedCarrier[]>([]);
  // The insured account, read to gate the rating screen on an incomplete floor
  // (BRK-S046). Best-effort: a failed read leaves the gate open.
  const [account, setAccount] = useState<AccountDetail | null>(null);
  // The product's manifest, resolved from the catalog by the submission's
  // product id (BRK-S052): undefined = still loading, null = no manifest
  // (a legacy product with no online rating).
  const [manifest, setManifest] = useState<ProductManifest | null | undefined>(
    undefined,
  );
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // The step the broker is VIEWING (BRK-S060), an ephemeral, session-scoped
  // choice — never persisted (NN #9): null follows the server-derived stage,
  // a value re-opens an already-completed step to review/edit. The derived
  // stage stays the source of truth for what's done; this only picks the panel.
  const [view, setView] = useState<FlowStage | null>(null);
  // The quote-stage profile-completion form's open state + a ref to scroll to
  // it, so the quote gate (BRK-S057) can route the broker straight into it.
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement | null>(null);
  const openProfile = useCallback(() => {
    setProfileOpen(true);
    requestAnimationFrame(() =>
      profileRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      }),
    );
  }, []);

  // The quotes Submit had PMS record (BRK-S393), taken once: the router state
  // keeps the fan-out but drops them, so a reload never requests them again.
  const [requested] = useState(() => handedRequests(location.state));
  useEffect(() => {
    if (requested.length === 0) return;
    navigate(
      { pathname: location.pathname, search: location.search },
      {
        replace: true,
        state: {
          ...(location.state as Record<string, unknown>),
          requested: [],
        },
      },
    );
    // Once, as the page opens with what Submit handed it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The top bar's breadcrumb names the submission (BRK-S358).
  usePageTrailName(detail?.submissionNumber);
  // Whether a carrier can be firmed up now, as the quote cards say, so a WC
  // submission's page can say a quote is next (BRK-S358).
  const [canFirmUp, setCanFirmUp] = useState(false);

  // Preview submission, held in the URL (BRK-S357). Closing it, however it
  // closes, puts focus back on the button that opens it.
  const preview = usePreviewPanel();
  // The Loss runs panel (BRK-S361), also in the URL: no quote's popup opens
  // under it.
  const lossRunsPanel = useLossRunsPanel();
  const previewButton = useRef<HTMLButtonElement | null>(null);
  const previewed = useRef(preview.open);
  useEffect(() => {
    if (previewed.current && !preview.open) {
      previewButton.current?.focus({ preventScroll: true });
    }
    previewed.current = preview.open;
  }, [preview.open]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [d, q] = await Promise.all([
        getSubmission(authedRequest, id),
        listSubmissionQuotes(authedRequest, id),
      ]);
      setDetail(d);
      setQuotes(q.items);
      setDeclined(q.declined);
      // Read the insured account to know whether its required floor is met, so
      // the rating screen can gate a draft (BRK-S046). A failed read is
      // non-fatal — the gate defaults open (see accountComplete below).
      if (d.account?.id) {
        try {
          setAccount(await getAccount(authedRequest, d.account.id));
        } catch {
          setAccount(null);
        }
      }
      // Resolve the product manifest that drives the details step (BRK-S052).
      // A failed catalog read degrades to the explicit not-available state.
      try {
        const { items } = await getProducts(authedRequest);
        setManifest(
          items.find((p) => p.id === d.product?.id)?.manifest ?? null,
        );
      } catch {
        setManifest(null);
      }
    } catch (err) {
      setLoadError(messageFor(err));
    } finally {
      setLoading(false);
    }
  }, [authedRequest, id]);

  useEffect(() => {
    void load();
  }, [load]);

  // BRK-S464 — a submitted Workers' Comp submission's tabs: Quotes, and
  // Actions, what Signal works for the broker on it (PMS-S872), read again
  // with the quotes. The URL holds the tab (`?tab=actions`) and the item a
  // notice names (`&action=`).
  const [searchParams, setSearchParams] = useSearchParams();
  const pageTabsId = useId();
  const tabbed =
    wcWizardEnabled() &&
    manifest?.lineOfBusiness === "workers_compensation" &&
    !!detail &&
    detail.status !== "draft";
  const actions = useActionItems(id, tabbed, quotes);
  // The carriers in play, for those Signal quotes by hand (BRK-S395): read
  // once submitted, and again with the quotes. A failed read shows none.
  const [carriers, setCarriers] = useState<SubmissionCarrier[]>([]);
  const readCarriers = useCallback(async () => {
    try {
      setCarriers(await getSubmissionCarriers(authedRequest, id));
    } catch {
      // The manual carriers' cards wait for the next read.
    }
  }, [authedRequest, id]);
  useEffect(() => {
    if (tabbed) void readCarriers();
  }, [tabbed, readCarriers]);
  const pageTab: PageTab =
    searchParams.get(TAB_PARAM) === "actions" ? "actions" : "quotes";
  const showTab = (next: PageTab) =>
    setSearchParams(
      (current) => {
        const params = new URLSearchParams(current);
        if (next === "actions") params.set(TAB_PARAM, next);
        else params.delete(TAB_PARAM);
        params.delete(ACTION_PARAM);
        return params;
      },
      { replace: true },
    );

  // Refresh just the persisted quotes (no full-page spinner) — QuoteComparison
  // calls this after auto-pulling an indication or requesting a quote.
  const refreshQuotes = useCallback(async () => {
    if (tabbed) void readCarriers();
    try {
      const { items, declined } = await listSubmissionQuotes(authedRequest, id);
      setQuotes(items);
      setDeclined(declined);
    } catch {
      // A failed background refresh of the list is non-fatal.
    }
  }, [authedRequest, id, tabbed, readCarriers]);

  if (loading) {
    return (
      <section>
        <p role="status" className="text-sm text-brk-dim">
          Loading submission…
        </p>
      </section>
    );
  }

  if (loadError || !detail) {
    return (
      <section className="flex flex-col gap-4">
        <p role="alert" className="text-sm text-brk-danger">
          {loadError ?? "Submission not found."}
        </p>
        <Link
          to="/submissions"
          className={`text-sm text-brk-accent hover:underline ${TOUCH_TARGET}`}
        >
          ← Back to submissions
        </Link>
      </section>
    );
  }

  // A submission is "rated" (quote-ready) once it has an effective date. Until
  // then, capture the manifest details instead of the pricing sections (BRK-S052).
  const rated = Boolean(detail.effectiveDate);

  // The STAGE-AWARE account floor (BRK-S057): the account-scope fields the
  // manifest requires at each stage. Step 2 needs only the INDICATION account
  // floor (address for WC — no email/phone/personnel); the QUOTE floor (what a
  // carrier needs to bind) is collected later, at the quote step. Defaults
  // open when the account or manifest couldn't be read, so a fetch failure never
  // blocks a legitimate submission (NN #7); the server stays the authority.
  const indicationAccountFloor: AccountFloorField[] = manifest
    ? accountFloorFields(manifest, "indication")
    : [];
  const quoteAccountFloor: AccountFloorField[] = manifest
    ? accountFloorFields(manifest, "quote")
    : [];
  const accountComplete =
    account && manifest
      ? accountMeetsFloor(indicationAccountFloor, account)
      : true;
  // The account-scope QUOTE-floor fields still unmet (BRK-S057) — drives the
  // quote gate + the completion affordance. Account-scoped, so tagged as
  // such for the shared checklist rendering.
  const quoteFloorMissing: MissingField[] =
    account && manifest
      ? unmetAccountFields(quoteAccountFloor, account).map((f) => ({
          key: f.key,
          label: f.label,
          scope: "account" as const,
        }))
      : [];

  // An incomplete insured (a draft that can't reach an indication yet) → capture
  // just the indication account floor inline, right here in the flow (BRK-S050 →
  // BRK-S057). Narrowed to non-null so the form gets a defined account.
  const incompleteAccount =
    !rated && account && manifest && !accountComplete ? account : null;

  // A Workers' Comp submission while the wizard is on (BRK-S305): the wizard is
  // where its application is filled in and edited, so this page sends a draft
  // there and shows a submitted one's comparison. No old details form.
  if (
    wcWizardEnabled() &&
    manifest?.lineOfBusiness === "workers_compensation"
  ) {
    const wcManifest = manifest;
    const existingAccountId =
      detail.insuredEditable === false || !insuredEditable(account, detail)
        ? detail.account?.id
        : null;
    const submitted = detail.status !== "draft";
    const insured = detail.account;
    // Where it stands, from the same derivation as the other lines' pages.
    const wcFlowStep = deriveFlowStep({
      accountComplete,
      rated,
      hasIndications: quotes.some((q) => q.kind === "indication"),
      hasQuotes: quotes.some((q) => q.kind === "quote"),
    });
    return (
      <LossRunsProvider submissionId={id} detail={detail}>
        <PageHeader
          // The insured is the title, its initials in the account record's
          // tile, with the list's chip (BRK-S358; Nav, 2026-10-14).
          leading={
            insured ? (
              <RecordAvatar text={initialsOf(insured.legalName)} />
            ) : undefined
          }
          title={
            insured ? (
              <Link to={`/accounts/${insured.id}`} className="hover:underline">
                {insured.legalName}
              </Link>
            ) : (
              <span className="tabular-nums">{detail.submissionNumber}</span>
            )
          }
          // Beside the chip, "Loss runs required" while they're asked for
          // (BRK-S361).
          status={
            <span className="flex flex-wrap items-center gap-2">
              <SubmissionStatusChip status={detail.status} outlined />
              <LossRunsPill />
            </span>
          }
          meta={<WcSubmissionMeta detail={detail} named={insured !== null} />}
          // Loss runs first, a draft's too (BRK-S361). Once submitted: what
          // Signal received, read-only (BRK-S357), and a button with a pencil
          // (BRK-S356; Nav, 2026-10-14: "Edit application doesn't have pencil
          // icon") opening Review.
          actions={
            <>
              <LossRunsButton />
              {submitted && (
                <>
                  <button
                    ref={previewButton}
                    type="button"
                    onClick={preview.show}
                    className={buttonClass({ variant: "secondary" })}
                  >
                    <Eye aria-hidden="true" className="h-4 w-4" />
                    Preview submission
                  </button>
                  <Link
                    to={wcStepPath(id, "business")}
                    className={buttonClass({ variant: "secondary" })}
                  >
                    <Pencil aria-hidden="true" className="h-4 w-4" />
                    Edit application
                  </Link>
                </>
              )}
            </>
          }
        />
        <section className="flex flex-col gap-6">
          {submitted && (
            <section
              aria-label="Where this submission is"
              className="flex flex-col gap-3 rounded-lg border border-brk-border bg-brk-panel p-4 md:flex-row md:items-center md:gap-8"
            >
              <div className="min-w-0 flex-1">
                <SubmissionFlowProgress current={wcFlowStep} />
              </div>
              {canFirmUp && (
                <p className="text-meta text-brk-dim md:max-w-xs">
                  <span className="font-semibold text-brk-text">
                    Next: a quote.
                  </span>{" "}
                  Open a carrier below to get one.
                </p>
              )}
            </section>
          )}
          <WcKeyFacts detail={detail} />
          {!submitted ? (
            <div className="flex flex-col gap-3 rounded-lg border border-brk-border bg-brk-panel p-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-body text-brk-text">
                This application is a draft. Finish it and submit it to get
                quotes.
              </p>
              <Link
                to={`/submissions/wc/${encodeURIComponent(id)}`}
                className={`${buttonClass()} self-start sm:self-auto`}
              >
                Continue the application
              </Link>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <Tabs
                value={pageTab}
                onChange={(_, next: PageTab) => showTab(next)}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                aria-label="Submission sections"
                sx={{ borderBottom: 1, borderColor: "divider" }}
              >
                <Tab
                  value="quotes"
                  label="Quotes"
                  id={`${pageTabsId}-tab-quotes`}
                  aria-controls={`${pageTabsId}-panel-quotes`}
                />
                <Tab
                  value="actions"
                  label={
                    <ActionsTabLabel
                      waiting={
                        actions.read.kind === "done"
                          ? waitingOnBroker(actions.read.items)
                          : 0
                      }
                    />
                  }
                  id={`${pageTabsId}-tab-actions`}
                  aria-controls={`${pageTabsId}-panel-actions`}
                />
              </Tabs>
              {/* Both stay mounted, hidden when not shown, so the comparison
                  keeps its runs and nothing is read twice. */}
              <div
                role="tabpanel"
                id={`${pageTabsId}-panel-quotes`}
                aria-labelledby={`${pageTabsId}-tab-quotes`}
                hidden={pageTab !== "quotes"}
              >
                <QuoteComparison
                  submissionId={id}
                  accountId={detail.account?.id ?? null}
                  quotes={quotes}
                  declined={declined}
                  onQuotesChanged={refreshQuotes}
                  quoteFloorMissing={quoteFloorMissing}
                  // The insured's details are the wizard's Business step's,
                  // where an insured on record is shown read-only (Nav,
                  // 2026-10-08).
                  onCompleteProfile={() =>
                    navigate(
                      existingAccountId &&
                        quoteFloorMissing.some((f) => f.key === "ein")
                        ? `/accounts/${encodeURIComponent(existingAccountId)}`
                        : wcStepPath(id, "business"),
                    )
                  }
                  initialOutcomes={
                    handedIndications(location.state) ?? undefined
                  }
                  missingFieldHref={(f) => {
                    return missingFieldPath(
                      f,
                      wcManifest,
                      id,
                      existingAccountId,
                    );
                  }}
                  popupBlocked={preview.open || lossRunsPanel.open}
                  onFirmUpChange={setCanFirmUp}
                  // Its carriers are asked on the wizard's last step, and the
                  // quotes Submit had PMS record are requested here (BRK-S393).
                  requestQuotesFor={requested}
                  questionsHref={wcStepPath(id, WC_LAST_STEP)}
                  carriers={carriers}
                  titleVisible={false}
                />
              </div>
              <div
                role="tabpanel"
                id={`${pageTabsId}-panel-actions`}
                aria-labelledby={`${pageTabsId}-tab-actions`}
                hidden={pageTab !== "actions"}
              >
                <SubmissionActions
                  submissionId={id}
                  read={actions.read}
                  reload={actions.reload}
                  retry={actions.retry}
                  quotes={quotes}
                  declined={declined}
                  focusActionId={
                    pageTab === "actions"
                      ? searchParams.get(ACTION_PARAM)
                      : null
                  }
                />
              </div>
            </div>
          )}
        </section>
        {submitted && preview.open && (
          <PreviewSubmission
            submissionId={id}
            insured={detail.account?.legalName ?? null}
            submissionNumber={detail.submissionNumber}
            submittedAt={detail.submittedAt}
            onClose={preview.close}
          />
        )}
      </LossRunsProvider>
    );
  }

  // The unified-flow step, derived purely from server state (BRK-S045, NN #9).
  const flowStep = deriveFlowStep({
    accountComplete,
    rated,
    hasIndications: quotes.some((q) => q.kind === "indication"),
    hasQuotes: quotes.some((q) => q.kind === "quote"),
  });

  // The step this page shows (BRK-S060). The DERIVED stage is the live position;
  // `completedStages` are the earlier steps that are now done (and so
  // revisitable); `activeStage` is what's on screen — the derived stage unless
  // the broker chose to re-open a completed one (bidirectional: back to a done
  // step, and forward again to where they left off). The derivation stays the
  // source of truth; `view` is ephemeral UI state only.
  const derivedStage = stageOfStep(flowStep);
  const completedStages: FlowStage[] = [
    ...(flowStep >= 3 ? (["profile"] as const) : []),
    ...(flowStep >= 4 ? (["rating"] as const) : []),
  ];
  const reachable = new Set<FlowStage>([...completedStages, derivedStage]);
  const activeStage: FlowStage =
    view && reachable.has(view) ? view : derivedStage;
  const revisiting = activeStage !== derivedStage;

  return (
    <>
      <PageHeader
        above={<SubmissionFlowProgress current={flowStep} />}
        title={<span className="tabular-nums">{detail.submissionNumber}</span>}
        meta={<SubmissionMeta detail={detail} />}
      />
      <section className="flex flex-col gap-6">
        {/* Revisiting a completed step: an explicit way back forward to the live
          stage (BRK-S060). */}
        {revisiting && (
          <div
            role="status"
            className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-brk-border bg-brk-bg px-4 py-3 text-sm"
          >
            <span className="text-brk-dim">
              Reviewing a completed step —{" "}
              <span className="font-medium text-brk-text">
                {STAGE_LABEL[activeStage]}
              </span>
              . Saving returns you to where you left off.
            </span>
            <button
              type="button"
              onClick={() => setView(null)}
              className={`shrink-0 font-medium text-brk-accent hover:underline ${TOUCH_TARGET}`}
            >
              Back to {STAGE_LABEL[derivedStage]} →
            </button>
          </div>
        )}

        {/* On the live stage, offer to re-open any already-completed step
          (BRK-S060). Not-yet-reachable steps simply aren't listed. */}
        {!revisiting && completedStages.length > 0 && (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
            <span className="text-brk-dim">Review a completed step:</span>
            {completedStages.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setView(s)}
                className={`font-medium text-brk-accent hover:underline ${TOUCH_TARGET}`}
              >
                {STAGE_EDIT_LABEL[s]}
              </button>
            ))}
          </div>
        )}

        {activeStage === "profile" ? (
          incompleteAccount ? (
            <InsuredProfileStep
              account={incompleteAccount}
              floorFieldKeys={indicationAccountFloor.map((f) => f.key)}
              onSaved={setAccount}
            />
          ) : account ? (
            // Revisiting a completed insured profile (BRK-S060): the full editable
            // form, saved through the same PUT seam; on save the derived stage
            // recomputes and we snap back to the live stage.
            <ProfileReview
              account={account}
              floorFieldKeys={indicationAccountFloor.map((f) => f.key)}
              onSaved={(fresh) => {
                setAccount(fresh);
                setView(null);
              }}
            />
          ) : (
            <p role="status" className="text-sm text-brk-dim">
              Loading insured profile…
            </p>
          )
        ) : activeStage === "rating" ? (
          manifest === undefined ? (
            <p role="status" className="text-sm text-brk-dim">
              Loading rating details…
            </p>
          ) : manifest === null ? (
            // A product with no registered manifest (legacy single-carrier GL):
            // there's no online details capture — handled, not an error (NN #7).
            <div className="rounded-md border border-brk-border bg-brk-bg px-3 py-2 text-sm text-brk-dim">
              Online rating isn’t available for this product yet — our team will
              follow up on this submission.
            </div>
          ) : (
            <>
              {!revisiting &&
                detail.carriedForwardKeys.length > 0 &&
                account && (
                  // The renewal account-review moment (BRK-S055): carried account
                  // info gets the same review opportunity as the carried bag.
                  <RenewalAccountReview
                    account={account}
                    onSaved={setAccount}
                  />
                )}
              <ManifestDetailsForm
                manifest={manifest}
                submissionId={detail.id}
                // The saved effective date lands on the typed spine column, so
                // merge it back into the bag when re-opening a rated submission —
                // it must not read as blank (BRK-S060). On the document path
                // (BRK-S038) the extracted bag prefills UNDER the server's data
                // (anything already saved wins) and is marked for review; skipped
                // when revisiting a completed step.
                initialBag={{
                  ...(!revisiting ? (documentPrefill?.bag ?? {}) : {}),
                  ...detail.data,
                  ...(detail.effectiveDate
                    ? { effectiveDate: detail.effectiveDate }
                    : {}),
                }}
                carriedKeys={revisiting ? [] : detail.carriedForwardKeys}
                extractedKeys={revisiting ? [] : documentPrefill?.extractedKeys}
                unmatchedCodes={
                  revisiting ? [] : documentPrefill?.unmatchedCodes
                }
                // Resolved class-code refs (PMS-S150) so carried-forward AND
                // document-extracted exposures prefill with the code + description,
                // not the canonical uuid (BRK-S062 / BRK-S038). The server's own
                // refs win; the document proposal's refs fill in the extracted bag
                // (not yet persisted, so not in dataDisplayRefs).
                codeRefs={{
                  ...(!revisiting ? (documentPrefill?.codeRefs ?? {}) : {}),
                  ...detail.dataDisplayRefs,
                }}
                onSubmit={async (bag) => {
                  setDetail(await setSubmissionDetails(authedRequest, id, bag));
                  setView(null);
                }}
              />
            </>
          )
        ) : (
          <>
            {account && manifest && quoteFloorMissing.length > 0 && (
              // The quote-stage account floor (BRK-S057): fields a carrier needs
              // to firm up a bindable quote (e.g. entity type / contact email for
              // PIE) that weren't required to price. Collected here — honestly
              // tied to when carriers need them — not front-loaded onto step 2.
              // The quote gate below opens/scrolls this via `openProfile`.
              <div ref={profileRef}>
                <QuoteProfileCompletion
                  account={account}
                  floorFieldKeys={quoteAccountFloor.map((f) => f.key)}
                  open={profileOpen}
                  onOpenChange={setProfileOpen}
                  onSaved={setAccount}
                />
              </div>
            )}
            <QuoteComparison
              submissionId={id}
              accountId={detail.account?.id ?? null}
              quotes={quotes}
              declined={declined}
              onQuotesChanged={refreshQuotes}
              quoteFloorMissing={quoteFloorMissing}
              onCompleteProfile={openProfile}
            />
          </>
        )}

        <Link
          to="/submissions"
          className={`text-sm text-brk-accent hover:underline ${TOUCH_TARGET}`}
        >
          ← Back to submissions
        </Link>
      </section>
    </>
  );
}

// The renewal account-review affordance (BRK-S055): the insured profile was
// carried from the prior term, so it gets a review OFFER — collapsed by
// default (the floor is met; this is not a gate), expanding to the same shared
// AccountForm used everywhere else (one capture surface, no duplicate form).
function RenewalAccountReview({
  account,
  onSaved,
}: {
  account: AccountDetail;
  onSaved: (fresh: AccountDetail) => void;
}) {
  const { identity, authedRequest } = useAuth();
  const [open, setOpen] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  if (confirmed) {
    return (
      <p role="status" className="text-sm text-brk-dim">
        Insured profile reviewed.
      </p>
    );
  }
  if (!open) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-brk-border bg-brk-panel p-4">
        <div className="flex min-w-0 flex-col">
          <span className="text-sm font-medium text-brk-text">
            Insured profile carried from the prior term
          </span>
          <span className="text-sm text-brk-dim">
            Worth a quick look before quoting — update anything that changed.
          </span>
        </div>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className={`${buttonClass({ variant: "secondary", size: "sm" })} shrink-0`}
        >
          Review insured profile
        </button>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h2 className="text-heading text-brk-text">
          Review the insured profile
        </h2>
        <p className="text-sm text-brk-dim">
          Carried from the prior term — confirm or update, then save.
        </p>
      </div>
      <AccountForm
        initial={account}
        withServicingBroker
        initialServicingBrokerId={
          account.servicingBroker?.id ?? identity?.brokerId ?? null
        }
        submitLabel="Save profile"
        pendingLabel="Saving…"
        onSubmit={async (input) => {
          onSaved(await updateAccount(authedRequest, account.id, input));
          setConfirmed(true);
        }}
        cancelSlot={
          <button
            type="button"
            onClick={() => setOpen(false)}
            className={`text-sm text-brk-accent hover:underline ${TOUCH_TARGET}`}
          >
            Close without saving
          </button>
        }
      />
    </div>
  );
}

// The quote-stage profile-completion affordance (BRK-S057). At the quote stage
// the insured needs the fields a carrier requires to bind (e.g. email, owners)
// that weren't needed to price. Collapsed by default (not a hard gate on the
// indication that's already showing), expanding to the shared AccountForm
// scoped to just the quote-stage account floor — one capture surface, honestly
// timed to when carriers actually need the detail.
function QuoteProfileCompletion({
  account,
  floorFieldKeys,
  open,
  onOpenChange,
  onSaved,
}: {
  account: AccountDetail;
  floorFieldKeys: string[];
  /** Controlled by the page so the quote gate can open + scroll it. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: (fresh: AccountDetail) => void;
}) {
  const { identity, authedRequest } = useAuth();

  if (!open) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-brk-border bg-brk-panel p-4">
        <div className="flex min-w-0 flex-col">
          <span className="text-sm font-medium text-brk-text">
            A quote needs a little more about the insured
          </span>
          <span className="text-sm text-brk-dim">
            Add it now to request a bindable quote — indications above don’t
            need it.
          </span>
        </div>
        <button
          type="button"
          onClick={() => onOpenChange(true)}
          className={`${buttonClass({ variant: "secondary", size: "sm" })} shrink-0`}
        >
          Complete the insured profile
        </button>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h2 className="text-heading text-brk-text">
          Complete the insured profile for a quote
        </h2>
        <p className="text-sm text-brk-dim">
          Carriers need these to bind — save, then request a quote.
        </p>
      </div>
      <AccountForm
        initial={account}
        withServicingBroker
        floorFieldKeys={floorFieldKeys}
        initialServicingBrokerId={
          account.servicingBroker?.id ?? identity?.brokerId ?? null
        }
        submitLabel="Save profile"
        pendingLabel="Saving…"
        onSubmit={async (input) => {
          onSaved(await updateAccount(authedRequest, account.id, input));
        }}
        cancelSlot={
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className={`text-sm text-brk-accent hover:underline ${TOUCH_TARGET}`}
          >
            Not now
          </button>
        }
      />
    </div>
  );
}

// The account-floor step (BRK-S046 gate → BRK-S050 inline form). A submission on
// a draft insured whose required floor isn't met can't be rated or priced. Rather
// than a gate that punts the broker to the read-only account page, we render the
// editable insured-profile form INLINE, right here in the flow (step 2): the
// broker completes it in place and the page advances to rating. It's the same
// shared AccountForm as New Business / account edit (one capture surface), with
// the servicing broker kept/defaulted to the logged-in broker. Placed on the
// detail so it protects every entry path (linear flow, a resumed draft, a
// bookmarked URL), consistent with the derived-from-server-state stepper.
function InsuredProfileStep({
  account,
  floorFieldKeys,
  onSaved,
}: {
  account: AccountDetail;
  floorFieldKeys: string[];
  onSaved: (fresh: AccountDetail) => void;
}) {
  const { identity, authedRequest } = useAuth();
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h2 className="text-heading text-brk-text">
          Complete the insured profile
        </h2>
        <p className="text-sm text-brk-dim">
          Just the insured’s address to get an indication — add anything else
          later.
        </p>
      </div>
      <AccountForm
        initial={account}
        withServicingBroker
        floorFieldKeys={floorFieldKeys}
        initialServicingBrokerId={
          account.servicingBroker?.id ?? identity?.brokerId ?? null
        }
        submitLabel="Save & continue"
        pendingLabel="Saving…"
        onSubmit={async (input) => {
          onSaved(await updateAccount(authedRequest, account.id, input));
        }}
      />
    </div>
  );
}

// Revisiting a completed insured profile (BRK-S060): the shared AccountForm in
// the same stage-floor mode as the initial capture (so it never demands fields
// a minimum-information account never collected), saved through the same PUT
// seam. On save the parent snaps back to the live stage.
function ProfileReview({
  account,
  floorFieldKeys,
  onSaved,
}: {
  account: AccountDetail;
  floorFieldKeys: string[];
  onSaved: (fresh: AccountDetail) => void;
}) {
  const { identity, authedRequest } = useAuth();
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h2 className="text-heading text-brk-text">Insured profile</h2>
        <p className="text-sm text-brk-dim">
          Review or update the insured, then save.
        </p>
      </div>
      <AccountForm
        initial={account}
        withServicingBroker
        floorFieldKeys={floorFieldKeys}
        initialServicingBrokerId={
          account.servicingBroker?.id ?? identity?.brokerId ?? null
        }
        submitLabel="Save profile"
        pendingLabel="Saving…"
        onSubmit={async (input) => {
          onSaved(await updateAccount(authedRequest, account.id, input));
        }}
      />
    </div>
  );
}

/** The header's facts: the status PMS stored, the product, the insured and
 *  the term. */
function SubmissionMeta({ detail }: { detail: SubmissionDetail }) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
      <StatusBadge status={detail.status} />
      {detail.product && <span>{detail.product.name}</span>}
      {detail.account && (
        <Link
          to={`/accounts/${detail.account.id}`}
          className={`text-brk-accent hover:underline ${TOUCH_TARGET}`}
        >
          {detail.account.legalName}
        </Link>
      )}
      {detail.effectiveDate && (
        <span>
          Effective {formatIsoDate(detail.effectiveDate)}
          {detail.expirationDate
            ? ` – ${formatIsoDate(detail.expirationDate)}`
            : ""}
        </span>
      )}
    </div>
  );
}

/** A WC submission's key facts (BRK-S358), with where its loss runs stand
 *  now, amber while they're asked for (BRK-S361). */
function WcKeyFacts({ detail }: { detail: SubmissionDetail }) {
  const lossRuns = useLossRuns()?.lossRuns ?? null;
  return (
    <StatStrip
      label="Key facts"
      stats={wcFacts(detail, lossRuns).map(({ label, value, attention }) => ({
        label,
        value: attention ? (
          <span className="text-amber-700 dark:text-amber-300">{value}</span>
        ) : (
          value
        ),
      }))}
    />
  );
}

/** A WC submission's line under its insured (BRK-S358): its number (unless
 *  it is the title, for a submission with no insured), its product, and when
 *  PMS took the Submit, as PMS stamped it. The term is among the key facts. */
function WcSubmissionMeta({
  detail,
  named,
}: {
  detail: SubmissionDetail;
  named: boolean;
}) {
  const parts = [
    named ? (
      <span key="number" className="tabular-nums">
        {detail.submissionNumber}
      </span>
    ) : null,
    detail.product ? <span key="product">{detail.product.name}</span> : null,
    detail.submittedAt ? (
      <span key="submitted">
        Submitted {formatShortDate(detail.submittedAt)}
      </span>
    ) : null,
  ].filter((part) => part !== null);
  return (
    <span>
      {parts.flatMap((part, i) => (i === 0 ? [part] : [" · ", part]))}
    </span>
  );
}

function StatusBadge({ status }: { status: string }) {
  return (
    <span className="rounded-full border border-brk-border bg-brk-bg px-2 py-0.5 text-xs font-medium text-brk-text">
      {statusLabel(status)}
    </span>
  );
}

function statusLabel(status: string): string {
  return status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
